import { wordById } from "@repo/hindi-shared";
import { useState } from "react";

import { DualLabel } from "../ui/DualLabel";
import { InkButton } from "../ui/InkButton";

import { gradeFromSelfMark } from "./grading";
import type { ExerciseProps } from "./types";

/**
 * WordReading — the word itself in large Tiro, prompting "पढ़िए — फिर
 * देखिए" (read it, then check). A "देखिए" reveal button flips in the IAST
 * transliteration + English gloss (Newsreader italic) and plays the word's
 * audio; the learner then self-marks whether they read it correctly, which
 * maps straight to {@link gradeFromSelfMark}.
 *
 * No timers here (reveal and mark are both direct clicks), so there's no
 * cancelledRef guard to wire up — unlike SoundToSymbol/SymbolToSound, which
 * complete after a delayed flash.
 */
export function WordReading({ exercise, audio, onComplete }: ExerciseProps) {
  const word = wordById.get(exercise.itemId);
  const [revealed, setRevealed] = useState(false);

  if (word === undefined) return null;

  function handleReveal(): void {
    setRevealed(true);
    void audio.speak(word!.devanagari);
  }

  function handleMark(ok: boolean): void {
    onComplete({ grade: gradeFromSelfMark(ok), itemId: exercise.itemId });
  }

  return (
    <div className="flex flex-col items-center gap-8 text-center">
      <p className="font-dev text-sm text-ink-soft uppercase">
        <DualLabel
          variant="inline"
          en="Read, then reveal"
          hi="पढ़िए — फिर देखिए"
        />
      </p>

      <p
        lang="hi"
        className="font-dev leading-none text-ink"
        style={{ fontSize: "clamp(96px, 12vw, 180px)" }}
      >
        {word.devanagari}
      </p>

      {!revealed && (
        <InkButton variant="sindoor" onClick={handleReveal}>
          <DualLabel variant="inline" en="Reveal" hi="देखिए" />
        </InkButton>
      )}

      {revealed && (
        <>
          <div>
            <p className="font-latin text-2xl text-ink-soft italic">
              {word.iast}
            </p>
            <p className="mt-1 font-latin text-base text-ink-soft">
              {word.gloss}
            </p>
          </div>

          <div className="flex flex-col items-center gap-3">
            <p className="font-latin text-sm text-ink-soft">
              <span lang="en">Did you read it correctly?</span>
            </p>
            <div className="flex gap-4">
              <InkButton variant="ghost" onClick={() => handleMark(false)}>
                <DualLabel variant="inline" en="✗ Try again" hi="फिर से" />
              </InkButton>
              <InkButton variant="sindoor" onClick={() => handleMark(true)}>
                <DualLabel variant="inline" en="✓ Correct" hi="सही" />
              </InkButton>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
